// StatusSection.jsx
import React, { useEffect, useState } from 'react';
import { dashboardService } from '../services/dashboard.service';

export default function StatusSection({ learningStats = [], loading: parentLoading = false, onPointsCalculated }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('Monthly');

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const data = await dashboardService.getSummary();
        setDashboardData(data);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  useEffect(() => {
    if (loading || !dashboardData) return;

    const gaugePointEl = document.getElementById("gauge-point");
    const rankStatusEl = document.querySelector(".rank-status");

    const updateGauge = (point, rankText) => {
      // ポイント数値更新
      if (gaugePointEl) gaugePointEl.innerText = point.toFixed(3);
      if (rankStatusEl) rankStatusEl.innerHTML = `ランキング: ${rankText} <span className="icon">🎓</span>`;

      // ゲージ角度と長さ更新
      const percentage = Math.min(Math.max(point / 10, 0), 1);
      const angle = percentage * 180 - 90;

      const needle = document.getElementById("gauge-needle");
      if (needle) needle.setAttribute("transform", `rotate(${angle},100,100)`);

      const arc = document.getElementById("gauge-active");
      const fullLength = 283;
      if (arc) arc.setAttribute("stroke-dasharray", `${percentage * fullLength} 999`);
    };

    // スコア計算
    const calculatePerformanceScore = (period) => {
      const { summary } = dashboardData;
      let score = 0;

      // 分数（学習時間）を取得
      const learningMinutes = summary.learningTime.total || summary.learningTime.last7Days || 0;
      
      // コース完了数を取得
      const completedCourses = summary.courses.completed || 0;

      // 新しい計算式: 分数*0.2 + コース*0.8
      const minutesScore = learningMinutes * 0.2;
      const coursesScore = completedCourses * 0.8;
      
      score = minutesScore + coursesScore;

      return Math.max(score, 0);
    };;

    const calculateRank = (score) => {
      if (score === 0) return "ランキング外";
      if (score >= 8) return "1位";
      if (score >= 6) return "3位";
      if (score >= 4) return "5位";
      if (score >= 2) return "8位";
      return "10位";
    };

    // 目盛り描画（毎回リセットして再描画）
    const ticksGroup = document.getElementById("ticks");
    if (ticksGroup) {
      ticksGroup.innerHTML = '';
      for (let i = 0; i <= 20; i++) {
        const tickAngle = -90 + (i * 180) / 20;
        const length = i % 5 === 0 ? 8 : 4;
        const x1 = 100 + Math.cos((tickAngle * Math.PI) / 180) * 75;
        const y1 = 100 + Math.sin((tickAngle * Math.PI) / 180) * 75;
        const x2 = 100 + Math.cos((tickAngle * Math.PI) / 180) * (75 - length);
        const y2 = 100 + Math.sin((tickAngle * Math.PI) / 180) * (75 - length);
        const tick = document.createElementNS("http://www.w3.org/2000/svg", "line");
        tick.setAttribute("x1", x1);
        tick.setAttribute("y1", y1);
        tick.setAttribute("x2", x2);
        tick.setAttribute("y2", y2);
        tick.setAttribute("stroke", "#bbb");
        tick.setAttribute("strokeWidth", "2");
        ticksGroup.appendChild(tick);
      }
    }

    // 選択期間に応じてスコア反映
    const score = calculatePerformanceScore(selectedPeriod);
    const rankText = calculateRank(score);
    updateGauge(score, rankText);

    // 計算されたポイントを親コンポーネントに通知
    if (onPointsCalculated) {
      onPointsCalculated(score);
    }

  }, [loading, dashboardData, selectedPeriod, onPointsCalculated]);

  // ラジオ変更
  const onPeriodChange = (e) => {
    const val = e.target.value;
    setSelectedPeriod(val);
    // スコアは useEffect 側で反映
  };

  return (
    <div className="status-section">
      {/* 学習時間 */}
      <div className="learning-box card">
        <div className="box-header">
          <h3>学習時間</h3>
          <div className="legend">
            <div><span className="dot dot-blue"></span> 学習</div>
            <div><span className="dot dot-lightblue"></span> テスト</div>
          </div>
        </div>
        <canvas id="learningChart"></canvas>
      </div>

      {/* パフォーマンス */}
      <div className="performance-box card">
        <div className="box-header">
          <h3>パフォーマンス</h3>
          <div className="legend">
            <div><span className="dot dot-blue"></span> 進捗状況</div>

            {/* ▼▼▼ ここをラジオUIに置き換え ▼▼▼ */}
            <div className="mydict">
              <div>
                <label>
                  <input
                    type="radio"
                    name="period-radio"
                    value="Monthly"
                    checked={selectedPeriod === "Monthly"}
                    onChange={onPeriodChange}
                  />
                  <span>Monthly</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="period-radio"
                    value="Weekly"
                    checked={selectedPeriod === "Weekly"}
                    onChange={onPeriodChange}
                  />
                  <span>Weekly</span>
                </label>
              </div>
            </div>
            {/* ▲▲▲ 置き換えここまで ▲▲▲ */}
          </div>
        </div>

        <div className="gauge-container">
          <svg className="gauge" viewBox="0 0 200 120">
            <defs>
              <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#42a5f5" />
                <stop offset="100%" stopColor="#1565c0" />
              </linearGradient>
            </defs>
            <path
              className="gauge-bg"
              d="M10,100 A90,90 0 0,1 190,100"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="8"
              strokeLinecap="round"
            />
            <path
              id="gauge-active"
              d="M10,100 A90,90 0 0,1 190,100"
              fill="none"
              stroke="url(#gaugeGradient)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray="0 999"
            />
            <line
              id="gauge-needle"
              x1="100"
              y1="100"
              x2="100"
              y2="20"
              stroke="#2196f3"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <g id="ticks" transform="rotate(-90 100 100)"></g>
            <circle cx="100" cy="100" r="10" fill="#fff" stroke="#2196f3" strokeWidth="5" />
          </svg>
        </div>

        <div className="score">
          <p>ポイント：<strong id="gauge-point">{loading ? "..." : "0.000"}</strong></p>
          <p className="rank-status">{loading ? "読み込み中..." : "ランキング計算中 🎓"}</p>
        </div>
      </div>
    </div>
  );
}
